// Dark-glass tokens — the immersive "North Star" surface language used by the
// signed-out AUTH surfaces (and reserved for the few other headline-moment
// surfaces: focus mode, big celebrations). This is the OPPOSITE of the default
// warm-white-canvas language; reach for it ONLY on those surfaces, never on
// ordinary list/form pages.
//
// Everything here is light-on-dark: surfaces and borders are translucent WHITE
// films over a charcoal canvas so depth reads as layered light, not stacked
// boxes. The on-glass text ramp is tuned for WCAG AA over the glass:
//   • ON_GLASS_STRONG — headings, values, primary body, input text.
//   • ON_GLASS        — body, helper, field labels (0.82 clears AA).
//   • ON_GLASS_SUBTLE — fine print + placeholders ONLY (0.6 AA floor).
//
// These mirror the redesign prototype kit (apps/web/src/redesign/kit) so the
// shipped product and the prototype stay visually identical. Production code
// imports from @spark/ui (here), never from redesign/**.

// Immersive canvas base — a radial deep-charcoal gradient. Compose with the two
// ambient glows below (as low-opacity radial-gradient overlays) for the canvas.
export const GLASS_CANVAS = '#0E1626'; // deepest charcoal base (focus-ring backstop)
export const GLASS_CANVAS_GRADIENT =
  'radial-gradient(140% 120% at 50% -10%, #1E2844 0%, #111827 42%, #0E1626 100%)';

// The two ambient glow colors that drift over the canvas. Use as the inner stop
// of a large, low-opacity radial-gradient.
export const GLASS_GLOW_PRIMARY = '#2563EB'; // cool blue, off-center upper area
export const GLASS_GLOW_SECONDARY = '#7CC5F2'; // softer cyan, lower area

// Frosted-glass card surface.
export const GLASS_BG = 'rgba(255,255,255,0.06)';
export const GLASS_BORDER = 'rgba(255,255,255,0.12)'; // hairline; wrap as `1px solid ${GLASS_BORDER}`
export const GLASS_SHADOW =
  '0 1px 0 rgba(255,255,255,0.05) inset, 0 40px 80px -32px rgba(0,0,0,0.65)';

// Translucent form fields on the glass — a visible (not hairline) border so the
// field reads clearly against the surface, and a brighter border + accent ring
// on focus.
export const GLASS_INPUT_BG = 'rgba(255,255,255,0.06)';
export const GLASS_INPUT_BG_FOCUS = 'rgba(255,255,255,0.10)';
export const GLASS_INPUT_BORDER = 'rgba(255,255,255,0.28)';
export const GLASS_INPUT_BORDER_FOCUS = '#93C5FD'; // blue-300

// On-glass white text ramp.
export const ON_GLASS_STRONG = 'rgba(255,255,255,0.98)'; // headings / values / input text
export const ON_GLASS = 'rgba(255,255,255,0.82)'; // body / labels (AA)
export const ON_GLASS_SUBTLE = 'rgba(255,255,255,0.6)'; // fine print / placeholder only

// Accent used for solid CTAs and the focus ring on the glass.
export const GLASS_ACCENT = '#2563EB'; // blue-600 — white text on it is ~5.2:1 (AA)
export const GLASS_FOCUS_RING = 'rgba(37,99,235,0.4)'; // accent at 0.4 — focus glow on fields

// Links on dark glass — blue-300 clears AA against the charcoal far better than
// the on-dark accent (#60A5FA), which is reserved for chrome.
export const GLASS_LINK = '#93C5FD'; // blue-300

// Danger ramp on dark glass. The lighter red (blue-free) clears AA over the
// charcoal where the light-surface #DC2626 would not. GLASS_DANGER is the
// inline-error / field-border ramp the on-dark form primitives use; the three
// GLASS_DANGER_WELL/BORDER/TEXT values compose the shared error banner
// (AUTH_TOAST_STYLE) as a translucent danger well.
export const GLASS_DANGER = '#FCA5A5'; // inline error text + invalid field border
export const GLASS_DANGER_WELL = 'rgba(220,38,38,0.16)'; // banner background (danger @ 0.16)
export const GLASS_DANGER_BORDER = 'rgba(252,165,165,0.45)'; // banner border (#FCA5A5 @ 0.45)
export const GLASS_DANGER_TEXT = '#FECACA'; // banner text (red-200, lighter over the well)
